import { useEffect, useRef, useState } from "react";
import { Brain, Zap, TrendingUp, Sparkles, X, Loader2, Send } from "lucide-react";
import api, { fmtNaira } from "@/lib/api";
import { toast } from "sonner";

/**
 * Business coach card + full-screen modal.
 * - Small stat tiles: Net profit (30d) + Runway risk
 * - First-message from `/coach/summary` shown collapsed on the card
 * - Tapping "Ask the coach" opens a full-screen chat that hits `/coach/ask`
 */
export default function BusinessCoach() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/coach/summary")
      .then((r) => setSummary(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-3xl bg-white border border-border p-5 shadow-sm animate-pulse">
        <div className="h-5 w-40 bg-kudi-cream rounded mb-4" />
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="h-20 bg-kudi-cream rounded-2xl" />
          <div className="h-20 bg-kudi-cream rounded-2xl" />
        </div>
        <div className="h-16 bg-kudi-cream rounded" />
      </div>
    );
  }
  if (!summary) return null;

  const runwayTone = {
    green: { bg: "bg-emerald-50 text-emerald-700", chip: "bg-emerald-100 text-emerald-800" },
    amber: { bg: "bg-amber-50 text-amber-700", chip: "bg-amber-100 text-amber-800" },
    red: { bg: "bg-rose-50 text-rose-700", chip: "bg-rose-100 text-rose-800" },
  }[summary.runway.color] || { bg: "bg-kudi-cream text-kudi-ink/70", chip: "bg-kudi-cream text-kudi-ink/60" };

  return (
    <>
      <div className="rounded-3xl bg-white border border-border p-5 shadow-sm" data-testid="business-coach-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
              <Brain className="w-4.5 h-4.5" strokeWidth={2.2} />
            </div>
            <h2 className="font-heading text-xl font-bold">Business coach</h2>
          </div>
          {summary.live && (
            <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold" data-testid="coach-live">
              <Zap className="w-3 h-3" /> Live
            </div>
          )}
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <StatTile
            label="Net profit (30d)"
            value={fmtNaira(summary.net_profit_30d || 0)}
            positive={summary.net_profit_30d > 0}
            testid="coach-stat-profit"
          />
          <StatTile
            label={summary.runway.label}
            value={summary.runway.note}
            tone={runwayTone.chip}
            testid="coach-stat-runway"
          />
        </div>

        {/* First message (collapsed to 3 lines) */}
        <div className="text-[15px] leading-relaxed text-kudi-ink/85 whitespace-pre-line line-clamp-3" data-testid="coach-first-msg">
          {summary.first_message}
        </div>

        <button
          onClick={() => setOpen(true)}
          className="mt-4 w-full bg-kudi-green hover:bg-kudi-green-dark text-white font-semibold rounded-2xl py-3 flex items-center justify-center gap-2 transition"
          data-testid="coach-open"
        >
          <Sparkles className="w-4 h-4" /> Ask the coach
        </button>
      </div>

      {open && <CoachChatModal onClose={() => setOpen(false)} initialMessage={summary.first_message} />}
    </>
  );
}

function StatTile({ label, value, positive, tone, testid }) {
  return (
    <div className={`rounded-2xl px-4 py-3 ${tone || "bg-kudi-cream/70"}`} data-testid={testid}>
      <div className="flex items-center gap-1 text-[11px] font-medium opacity-70">
        <TrendingUp className={`w-3 h-3 ${positive ? "text-emerald-600" : ""}`} />
        {label}
      </div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
}

function CoachChatModal({ onClose, initialMessage }) {
  const [messages, setMessages] = useState([
    { id: "welcome", role: "assistant", text: initialMessage },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  async function send(e) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { id: `u_${Date.now()}`, role: "user", text }]);
    setSending(true);
    try {
      const r = await api.post("/coach/ask", { text, session_id: sessionId || undefined });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, r.data.assistant_message]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Coach is offline right now. Try again in a minute.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="coach-modal">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg h-[92vh] sm:h-[80vh] sm:rounded-3xl rounded-t-3xl flex flex-col shadow-2xl"
      >
        <header className="p-4 border-b border-border flex items-center gap-3 shrink-0">
          <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
            <Brain className="w-4.5 h-4.5" strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-base">Business coach</div>
            <div className="text-[11px] text-kudi-ink/50">Personalized to your Kobolog data</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-kudi-cream flex items-center justify-center" data-testid="coach-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-kudi-cream/40">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`${m.role === "user"
                  ? "bg-kudi-green text-white rounded-2xl rounded-br-[6px]"
                  : "bg-white text-kudi-ink rounded-2xl rounded-bl-[6px] border border-border/60"
                } px-3.5 py-2.5 max-w-[82%] shadow-sm text-[15px] leading-relaxed whitespace-pre-wrap`}
                data-testid={`coach-msg-${m.role}`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white border border-border/60 rounded-2xl rounded-bl-[6px] px-3.5 py-2.5 text-kudi-ink/50 text-sm inline-flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
              </div>
            </div>
          )}
        </div>

        <form onSubmit={send} className="p-3 border-t border-border shrink-0 flex gap-2 bg-white">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask the coach anything about your business…"
            className="flex-1 bg-kudi-cream/70 border border-transparent rounded-full px-4 py-2.5 text-sm focus:border-kudi-green focus:outline-none"
            disabled={sending}
            data-testid="coach-input"
          />
          <button type="submit" disabled={sending || !input.trim()} className="w-10 h-10 rounded-full bg-kudi-green text-white flex items-center justify-center disabled:opacity-40 hover:bg-kudi-green-dark transition" data-testid="coach-send">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
